import { SharedColors } from "apparatus/library/common";
import { Deselect, DeselectAndSelect, Select, UpdateAppearanceProperties } from "command/basic";
import { commandProcessor, Sequence } from "command/command";
import { getDescendantIDs, getRootGroup } from "editing/grouping/operations";
import { InteractableMatcher } from "editing/interaction";
import { RotateItems } from "editing/rotation/rotate_command";
import { evaluatePrecomputedSnappings, precomputeSnapping } from "editing/snapping/compute";
import { Key, Point, project, Rectangle, Shape, Size, Tool } from "paper";
import { ID } from "store/id";
import appStore from "store/store";
import { Move } from "./move";
/** Stronger snap distance for zoom level 1. */
const STRONG_SNAP_DISTANCE = 20.0;
class MultiTool extends Tool {
    constructor(layers, app) {
        super();
        // Selection box (visual).
        this.selectionBox = (() => {
            let box = Shape.Rectangle(new Point(0, 0), new Size(0, 0));
            // Style the selection box.
            box.strokeColor = SharedColors.selectionGreen;
            box.fillColor = SharedColors.selectionGreen + "40"; // With alpha
            box.strokeWidth = 1.0;
            box.radius = 0;
            box.visible = false;
            return box;
        })();
        this.onMouseDown = (event) => {
            // On mouse down, there are 2 options:
            // A) Down on an apparatus
            //    a) Apparatus already selected.
            //       1) If moved, then move current selection.
            //       2) Else, no change (apparatus remains selected)
            //    b) Apparatus not already selected
            //       1) If moved, then move this apparatus only
            //       2) Else, select this apparatus
            // b) Down on empty space
            //    Draw selection box.
            commandProcessor.startRecording();
            this.operation = this.startOperation(event);
            if (this.operation.type == "moving" || this.operation.type == "adjusting") {
                // We hide the HTLM-based selection box, but only when moving or adjusting
                this.appInterface.setIsInteracting(true);
                // Set the state to moving.
                this.appInterface.setIsMovingItems(true);
            }
        };
        this.onMouseDrag = (event) => {
            var _a;
            if (!this.operation)
                return;
            // Vector from downPoint to current point.
            let deltaVector = event.point.subtract(event.downPoint);
            switch (this.operation.type) {
                case "moving": {
                    const adjustedResult = this.deltaVector(deltaVector, this.operation.snapping, { lock90degrees: true });
                    const moveVector = adjustedResult.delta;
                    this.operation.movedComponents.forEach(c => c.setPositionDelta(moveVector));
                    this.appInterface.setGlobalSnapViz((_a = adjustedResult.viz) !== null && _a !== void 0 ? _a : { lines: [] }); // TODO Add sentinel
                    break;
                }
                case "adjusting": {
                    const adjustedResult = this.deltaVector(deltaVector, this.operation.snapping, { lock90degrees: false });
                    this.operation.adjustable.setDelta(adjustedResult.delta);
                    break;
                }
                case "selection":
                case "selection_on_top":
                    // Draw the selection box.
                    this.selectionBox.visible = true;
                    this.selectionBox.size = new Size(deltaVector).abs();
                    this.selectionBox.position = event.downPoint.add(deltaVector.divide(2.0));
            }
        };
        this.onMouseUp = (event) => {
            this.selectionBox.visible = false;
            if (!this.operation) {
                this.appInterface.setIsInteracting(false);
                this.appInterface.setIsMovingItems(false);
                commandProcessor.finishRecording();
                return;
            }
            switch (this.operation.type) {
                case "moving": {
                    // Apparatus have been moved. Commit this change as a command.
                    // Vector from downPoint to current point. If it's a zero vector, skip
                    var deltaVector = event.point.subtract(event.downPoint);
                    if (deltaVector.isZero())
                        break;
                    const adjustedDelta = this.deltaVector(deltaVector, this.operation.snapping, { lock90degrees: true });
                    deltaVector = adjustedDelta.delta;
                    // Create a move command for each moved apparatus.
                    let moveCommand = new Move(this.operation.movedComponents.map(c => c.id), deltaVector);
                    // Check if there are appearance changes.
                    let appearanceChangeCommands = [];
                    if (adjustedDelta.appearanceChange) {
                        const id = adjustedDelta.appearanceChange.id;
                        const item = appStore.items().filter(ID.matching(id))[0];
                        if (item) {
                            const prevSpecs = item.appearance;
                            const newSpecs = adjustedDelta.appearanceChange.partialSpecs;
                            appearanceChangeCommands = [
                                new UpdateAppearanceProperties(id, newSpecs, prevSpecs),
                                new RotateItems([{ id: id, rotation: adjustedDelta.appearanceChange.rotation, originalRotation: item.rotation }])
                            ];
                        }
                    }
                    if (appearanceChangeCommands.length > 0) {
                        commandProcessor.execute(new Sequence([moveCommand, ...appearanceChangeCommands]));
                    }
                    else {
                        commandProcessor.execute(moveCommand);
                    }
                    break;
                }
                case "adjusting": {
                    let delta = event.point.subtract(event.downPoint);
                    if (delta.isZero()) {
                        if (this.operation.adjustable.onNoop) {
                            const command = this.operation.adjustable.onNoop();
                            if (command) {
                                commandProcessor.execute(command);
                            }
                        }
                        break;
                    }
                    const adjustedDelta = this.deltaVector(delta, this.operation.snapping, { lock90degrees: false });
                    let command = this.operation.adjustable.onEnd(adjustedDelta.delta);
                    commandProcessor.execute(command);
                    break;
                }
                case "selection":
                case "selection_on_top":
                    // Complete selection.
                    let selectionHitRectangle = new Rectangle(event.downPoint, event.point);
                    if (selectionHitRectangle.isEmpty() && this.operation.type == "selection")
                        break;
                    // If a hit rectangle is available, use getItems, otherwise hit test 1 item only.
                    let interactableItems;
                    if (!selectionHitRectangle.isEmpty()) {
                        interactableItems = project.getItems({
                            overlapping: selectionHitRectangle,
                            match: InteractableMatcher,
                        });
                    }
                    else {
                        let hitResult = project.hitTest(event.downPoint, {
                            fill: true,
                            bounds: true,
                            stroke: true,
                            match: InteractableMatcher,
                        });
                        if (hitResult) {
                            interactableItems = [hitResult.item];
                        }
                        else {
                            interactableItems = [];
                        }
                    }
                    let components = interactableItems.filter(i => i.interaction.type == "selectAndMove").map(i => i.interaction.component);
                    if (components.length > 0) {
                        if (this.operation.type == "selection") {
                            const ids = convertToIdsExpandingGroups(components);
                            commandProcessor.execute(new Select(ids));
                        }
                        else {
                            // Check if the newly selected items are all already selected. If so,
                            // remove them from the selection. Otherwise select them.
                            let notSelectedComponents = components.filter(c => !c.isSelected);
                            if (notSelectedComponents.length > 0) {
                                // Select these items.
                                let notSelectedIds = convertToIdsExpandingGroups(notSelectedComponents);
                                commandProcessor.execute(new Select(notSelectedIds));
                            }
                            else {
                                let ids = convertToIdsExpandingGroups(components);
                                commandProcessor.execute(new Deselect(ids));
                            }
                        }
                    }
                    break;
            }
            // Disable visualisation of snap points.
            for (let data of appStore.items()) {
                let apparatus = this.appInterface.getApparatusComponent(data.id);
                apparatus === null || apparatus === void 0 ? void 0 : apparatus.setState({ visualiseSnapping: undefined });
            }
            this.appInterface.setIsInteracting(false);
            this.appInterface.setIsMovingItems(false);
            this.appInterface.setGlobalSnapViz();
            commandProcessor.finishRecording();
            this.operation = undefined;
        };
        this.appInterface = app;
        layers.overlay.addChild(this.selectionBox);
    }
    // Private helpers.
    startOperation(event) {
        if (Key.modifiers.shift) {
            return { type: "selection_on_top" };
        }
        let foundTarget = project.hitTest(event.downPoint, {
            fill: true,
            bounds: true,
            stroke: true,
            match: InteractableMatcher,
        });
        // Resolve the apparatus.
        if (foundTarget == null) {
            // Down event landed on empty space.
            // Deselect currently selected items.
            let deselectIds = appStore.selectedObjectsID();
            if (deselectIds.length > 0) {
                commandProcessor.execute(new Deselect(deselectIds));
            }
            return { type: "selection" };
        }
        // Otherwise down event landed on an item.
        let foundInteractable = foundTarget.item;
        // First determine what type of interaction is associated with this component.
        let interaction = foundInteractable.interaction;
        switch (interaction.type) {
            case "selectAndMove":
                // Determine if this apparatus is already selected.
                let component = interaction.component;
                if (!component.isSelected) {
                    // Deselect currently selected items, if any.
                    let deselectIds = appStore.selectedObjectsID();
                    // Determine if this item is part of a group.
                    let selectIds;
                    if (component.parentGroup) {
                        // This component is part of a group. Select all other
                        // grouped components.
                        const rootGroup = getRootGroup(component.parentGroup);
                        selectIds = getDescendantIDs(rootGroup);
                    }
                    else {
                        // Select just this apparatus.
                        selectIds = [component.id];
                    }
                    commandProcessor.execute(new DeselectAndSelect({
                        select: selectIds,
                        deselect: deselectIds
                    }));
                }
                // Retrieve components of currently selected items.
                let selectedItems = appStore.selectedObjectsID()
                    .map(id => this.appInterface.getObjectComponent(id));
                // Snapping:
                // 1) Get non-selected apparatus
                // 2) For every snap point in the selection, find the delta to every snap point
                //    in the non-selected apparatus.
                const { selected: selectedApparatus, other } = this.getSelectedAndOtherApparatusComponents();
                let snapping = [];
                snapping = precomputeSnapping(selectedApparatus, other);
                return {
                    type: "moving",
                    movedComponents: selectedItems,
                    snapping: snapping,
                };
            case "adjust":
                {
                    let precomputedSnapping = [];
                    if (interaction.component.onStart) {
                        const { snappableComponent } = interaction.component.onStart();
                        if (snappableComponent) {
                            const { other } = this.getSelectedAndOtherApparatusComponents();
                            precomputedSnapping = precomputeSnapping([snappableComponent], other);
                        }
                    }
                    return {
                        type: "adjusting",
                        adjustable: interaction.component,
                        snapping: precomputedSnapping,
                    };
                }
        }
    }
    /**
     * Returns the delta vector to use for movement, taking into account
     * potential snapping points. Potentially, other properties may also be changed.
     */
    deltaVector(rawDeltaVector, snapping, options) {
        // If Shift is held down, snap the vector to horizontal/vertical
        if (options.lock90degrees && Key.modifiers.shift) {
            rawDeltaVector.angle = Math.round(rawDeltaVector.angle / 90) * 90;
        }
        // Snapping is disabled by holding down Ctrl.
        if (Key.modifiers.control || Key.modifiers.command) {
            return { delta: rawDeltaVector };
        }
        // Evaluate snapping.
        if (snapping.length > 0) {
            const snappedData = evaluatePrecomputedSnappings(rawDeltaVector, snapping, STRONG_SNAP_DISTANCE / appStore.zoom());
            if (snappedData) {
                return snappedData;
            }
        }
        return { delta: rawDeltaVector };
    }
    getSelectedAndOtherApparatusComponents() {
        let otherApparatus = [];
        let selectedApparatus = [];
        for (let data of appStore.items()) {
            let apparatus = this.appInterface.getApparatusComponent(data.id);
            if (!apparatus)
                continue;
            // Don't use apparatus.isSelected, it can be stale compared to the state.
            if (data.selected) {
                selectedApparatus.push(apparatus);
            }
            else {
                otherApparatus.push(apparatus);
            }
        }
        return { selected: selectedApparatus, other: otherApparatus };
    }
}
/**
 * Turns a list of components into IDs. If any components are part of a group,
 * the group is expanded fully, including descednant IDs in the return value.
 */
function convertToIdsExpandingGroups(components) {
    let ids = [];
    components.forEach(c => {
        ids.push(...(c.parentGroup ? getDescendantIDs(getRootGroup(c.parentGroup)) : [c.id]));
    });
    return ids;
}
export default MultiTool;
